import { Icon, Paragraph, Radio, Tooltip } from 'components/patterns'
import PropTypes from 'prop-types'
import React from 'react'
import styled from '@emotion/styled'
import strings from 'utils/strings'
import { CAN_OUTPUT } from 'utils/constants'
import { themeColor } from 'utils/styles'
import {
  StyledFeatureTable,
  TableHeader,
} from './AddRemoveVehicleFeatures.style'

// Matches the tooltip-question-circle styling used in FeatureRevisionParameter
// and PartNumberSearch: white circle background, blue question mark.
const TooltipIcon = styled(Icon)`
  path:first-of-type {
    fill: ${themeColor('whitePrimary')};
  }
  path:nth-of-type(2) {
    fill: ${themeColor('bluePrimary')};
  }
`

const ConflictTable = ({
  option,
  data,
  view,
  allFeatures,
  selection,
  onSelect,
  label,
  statusLabels,
}) => {
  const featuresWith = {
    activeHigh: strings.active_high_polarity,
    activeLow: strings.active_low_polarity,
    res_0_100k: strings.res_0_100k,
    res_0_500: strings.res_0_500,
    volt_0_6: strings.volt_0_6,
    volt_0_16_rel: strings.volt_0_16_rel,
    volt_0_18k5: strings.volt_0_18k5,
  }

  const selectedOptionInformational = {
    activeHigh: strings.active_high_polarity_selected_informational,
    activeLow: strings.active_low_polarity_selected_informational,
    res_0_100k: strings.selecting_this_configuration_will_remove,
    res_0_500: strings.selecting_this_configuration_will_remove,
    volt_0_6: strings.selecting_this_configuration_will_remove,
    volt_0_16_rel: strings.selecting_this_configuration_will_remove,
    volt_0_18k5: strings.selecting_this_configuration_will_remove,
  }

  const featuresMap = allFeatures.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.uri]: curr,
    }),
    {}
  )

  const showTableHeader = view !== 'digitalOutput' && view !== CAN_OUTPUT
  const addRadioColumn = view === 'digitalOutput'

  const isCanOutputView = view === CAN_OUTPUT

  const columns = [
    {
      key: 'partNumber',
      title: strings.feature_no,
      dataIndex: 'partNumber',
      width: 140,
    },
    {
      key: 'revision',
      title: strings.revision_short_dot,
      dataIndex: 'revision',
      width: 50,
      render: (revision) => revision || '-',
    },
    {
      key: 'featureDescription',
      title: strings.service_description,
      dataIndex: 'featureDescription',
    },
    {
      key: 'featureUri',
      title: (
        <>
          {isCanOutputView ? strings.state : strings.status}{' '}
          <Tooltip
            overlayStyle={{ width: 380, maxWidth: 380 }}
            title={
              <div style={{ width: 380 }}>
                <Paragraph contrast>
                  <strong>{strings.new_feature}</strong>:{' '}
                  {(statusLabels && statusLabels.whatIsNewFeature) ||
                    strings.what_is_new_feature}
                </Paragraph>
                <Paragraph contrast>
                  <strong>
                    {(statusLabels && statusLabels.onEntity) ||
                      strings.on_vehicle}
                  </strong>
                  :{' '}
                  {(statusLabels && statusLabels.whatIsOnEntity) ||
                    strings.what_is_on_vehicle}
                </Paragraph>
                {!statusLabels && (
                  <Paragraph contrast>
                    <strong>{strings.factory_defined}</strong>:{' '}
                    {strings.what_is_factory_defined}
                  </Paragraph>
                )}
              </div>
            }
          >
            <TooltipIcon
              icon="tooltip-question-circle"
              style={{ verticalAlign: 'middle' }}
            />
          </Tooltip>
        </>
      ),
      dataIndex: 'featureUri',
      width: 250,
      render: (uri) => {
        const feature = featuresMap[uri]
        if (feature) {
          if (feature.isMarkedForAddition)
            return strings.default_feature_description
          if (feature.asBuilt) return strings.factory_defined_on_vehicle

          return (statusLabels && statusLabels.onEntity) || strings.on_vehicle
        }
        return strings.default_feature_description
      },
    },
  ]

  if (isCanOutputView) {
    columns.unshift({
      key: 'isSelected',
      title: '',
      dataIndex: 'featureId',
      width: 42,
      render: (featureId) => (
        <Radio
          checked={featureId === selection}
          className="link"
          name="selection"
          onClick={() => onSelect(featureId)}
        />
      ),
    })
  }

  return (
    <>
      {showTableHeader && (
        <TableHeader>
          <Radio
            checked={option === selection}
            className="link"
            name="selection"
            onClick={() => onSelect(option, view)}
          />
          <Paragraph
            bold
            style={{ fontSize: 16, display: 'inline-block', marginLeft: 8 }}
          >
            {strings.features_with} &quot;{featuresWith[option]}&ldquo;
          </Paragraph>
          {option === selection && (
            <Paragraph
              style={{ fontSize: 16, display: 'inline-block', marginLeft: 8 }}
            >
              {selectedOptionInformational[option]}
            </Paragraph>
          )}
        </TableHeader>
      )}
      {addRadioColumn && (
        <TableHeader>
          <Radio
            checked={option === selection}
            className="link"
            name="selection"
            onClick={() => onSelect(option, view)}
          />
          <Paragraph
            bold
            style={{ fontSize: 16, display: 'inline-block', marginLeft: 8 }}
          >
            {label}
          </Paragraph>
          {option === selection && (
            <Paragraph
              style={{ fontSize: 14, display: 'inline-block', marginLeft: 8 }}
            >
              {strings.selecting_this_output_configuration_will_remove}
            </Paragraph>
          )}
        </TableHeader>
      )}
      <StyledFeatureTable
        columns={columns}
        dataSource={data}
        rowKey={(record, index) => `${record.id}-${index}`}
      />
    </>
  )
}

ConflictTable.propTypes = {
  option: PropTypes.string,
  data: PropTypes.array,
  view: PropTypes.string,
  allFeatures: PropTypes.array,
  selection: PropTypes.string,
  onSelect: PropTypes.func,
  label: PropTypes.string,
  /** Optional overrides for the status column labels.
   * Keys: onEntity (replaces 'On Vehicle'), whatIsOnEntity, whatIsNewFeature */
  statusLabels: PropTypes.object,
}

export default ConflictTable
